// This is a part of the Cadcorp SIS source code. 
// Copyright (c) NEC Software Solutions UK Limited 1991-2026 
import { Overlay } from './Overlay';
import { IAjax, IExpressionBuilder, IQueryResponse, IQueryHelper } from '../../../../CommonScripts/Abstractions';
import { Item } from './Item';
declare const ol: any;
/**
* Handles queries on individual GeognoSIS overlays.
*/
export class QueryHelper implements IQueryHelper{
    /**
    * @property {object} overlayInfo - Information about the overlay.
    * @memberof Cadcorp.Web.Entities.GeognoSIS.QueryHelper#
    * @private
    */
    protected overlayInfo: { sessionId: string, index: number, name: string };
    /**
    * @property {string} mapName - The name of the map.
    * @memberof Cadcorp.Web.Entities.GeognoSIS.QueryHelper#
    * @public
    */
    readonly mapName: string;

    /**
     * @property {IAjax} ajaxWrapper An IAjax implementation to perform asynchronous requests
     * @private
     */
    protected ajaxWrapper: IAjax;
    /**
     * @property {string} queryEndpoint The url to use to perform the query
     * @public
     */
    readonly queryEndpoint: string;
    /**
     * @property {string} nexyFeaturesEndpoint The url to use to get the next features in the results set
     * @public
     */
    readonly nextFeaturesEndpoint: string;
    /**
     * @property {number} epsgCode The epsgcode of the layer to perform the query on
     * @public
     */
    readonly epsgCode: number;
    /**
     * Constructor for Cadcorp based classes.
     * @constructor
     * @param {object} overlayInfo Information about the overlay
     * @param {IAjax} ajaxWrapper An IAjax implementation to perform asynchronous requests
     * @param {string} queryEndpoint The url to use to perform the query
     * @param {number} epsgCode The epsgcode of the layer to perform the query on
     * @param {string} mapName The name of the map
     * @public
     */
    constructor(overlayInfo: { sessionId: string, index: number, name: string }, ajaxWrapper: IAjax, queryEndpoint: string, nextFeaturesEndpoint: string, epsgCode: number, mapName:string) {
        this.overlayInfo = overlayInfo;
        this.ajaxWrapper = ajaxWrapper;
        this.queryEndpoint = queryEndpoint;
        this.epsgCode = epsgCode;
        this.mapName = mapName;
        this.nextFeaturesEndpoint = nextFeaturesEndpoint;
        //this.sessionId = null;
        //this.sessionId = this.overlay.sessionId;
    }
    /**
     * @inheritdoc 
     */
    findItems(map: any, expressionBuilder: IExpressionBuilder, maxFeaturesToGet: number, startPosition: number, queryBounds?: number[], ignoreBbox?: boolean, abort?: AbortController): Promise<IQueryResponse> {
        const queryData = this.getQueryData(map, expressionBuilder, maxFeaturesToGet, startPosition, queryBounds, ignoreBbox);
        let options = null;
        if (abort)
            options = { signal: abort.signal };
        //use post so we can put the potentially huge expression as the body not on the url
        return this.ajaxWrapper.post(this.queryEndpoint, queryData, options).
            then((response) => {
                return(this.queryCompleted(response.data));
            });
    }

    protected getQueryData(map: any, expressionBuilder: IExpressionBuilder, maxFeaturesToGet: number, startPosition: number, queryBounds?: number[], ignoreBbox?: boolean): any {

        const sisExpression = expressionBuilder.createExpression(map);
        const queryData = {
            MaxFeaturesToGet: maxFeaturesToGet,
            Expression: sisExpression,
            StartPosition: startPosition
        };

        if (!ignoreBbox) {
            const bbox = this.getbbox(map, queryBounds);
            queryData['CRS'] = this.epsgCode.toString(),
            queryData['Bbox'] = bbox;
        }

        return queryData;
    }

    /**
     *Gets the bbox parameter
     */
    protected getbbox(map: any, queryBounds?: number[]): string {
        let bbox = '';
        const view = map.getView();
        const mapProjection = view.getProjection();

        const destinationPrj = `EPSG:${this.epsgCode}`;
        const needTransformation = (destinationPrj !== mapProjection.getCode());

        //make sure the bbox is in the correct crs
        if (queryBounds) {
            if (needTransformation) {
                bbox = '&BBOX=' + ol.proj.transformExtent(queryBounds, mapProjection.getCode(), destinationPrj).join(',');
            }
            else
                bbox = '&BBOX=' + queryBounds.join(',');
        }
        else {
            if (needTransformation) {
                bbox = '&BBOX=' + ol.proj.transformExtent(view.calculateExtent(map.getSize()), mapProjection.getCode(), destinationPrj).join(',');
            }
            else
                bbox = '&BBOX=' + view.calculateExtent(map.getSize()).join(',');

        }
        return bbox;
    }

    /**
     * Event Handler for when the query is completed.
     * @protected
     * @param {Object} e - event args.
     * @memberof Cadcorp.Web.Entities.GeognoSIS.QueryHelper#
     */
    protected queryCompleted(e: any): IQueryResponse {
        let returnObj = null;
        const layerName = this.overlayInfo.name;
        if (e.features && e.features.length > 0) {
            const foundItems: Item[] = [];

            for (let a = 0; a < e.features.length; a++) {
                const feature = e.features[a];
                const item = new Item({
                    feature: feature,
                    layerIndex: this.overlayInfo.index,
                    sessionId: this.overlayInfo.sessionId,
                    mapName: this.mapName
                });
                foundItems.push(item);
            }
            if (e.next) {
                returnObj = { foundItems: foundItems, next: decodeURIComponent(e.next), name:layerName };
            }
            else
                returnObj = { foundItems: foundItems, next: null, name: layerName };

        }
        else {
            returnObj = { foundItems: [], next: null, name: layerName };
        }
        return returnObj;
    }
    /**
     * Gets the next chunk for  a query.
     * @public
     * @param {string} featureUrl - The "next" property of the response.
     * @memberof Cadcorp.Web.Entities.GeognoSIS.QueryHelper#
     */
    getNextPage(featureUrl: string): Promise<IQueryResponse> {

        // the url is escaped so decode it
        featureUrl = decodeURIComponent(featureUrl);
        const queryData = {
            Next: featureUrl
        };
        const url = this.nextFeaturesEndpoint;
        //Post to the controller
        return this.ajaxWrapper.post(url, queryData).then((response) => {
            return (this.queryCompleted(response.data));
        });

    }
    /**
     * Destroys the object.
     * @public
     * @export
     * @memberof Cadcorp.Web.Entities.GeognoSIS.QueryHelper#
     */
    destroy() {
        this.ajaxWrapper = null;
        this.overlayInfo = null;
    }


};

